import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, FileText, CreditCard, History, Plus, AlertTriangle, CheckCircle, Clock, X, Calendar, User, RefreshCw, Pencil, Trash2, Ban } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ReasonDialog, RenewalDialog, PaymentDialog, newRequestId, todayStr,
  formatCurrency, formatDate, formatDateTime, termTotal, TERMS
} from '../components/subscription/SubscriptionDialogs';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api/subscription-management`;

const STATUS_COLORS = {
  'Aranacak': 'bg-blue-100 text-blue-700',
  'Takipte': 'bg-yellow-100 text-yellow-700',
  'Teklif verildi': 'bg-purple-100 text-purple-700',
  'Ertelendi': 'bg-orange-100 text-orange-700',
  'Yenilemeyecek': 'bg-red-100 text-red-700',
  'Yenileme yok': 'bg-gray-100 text-gray-600',
  'Kısmi yenilendi': 'bg-amber-100 text-amber-700',
  'Tam yenilendi': 'bg-green-100 text-green-700',
  'Tahsilat yok': 'bg-gray-100 text-gray-600',
  'Kısmi tahsilat': 'bg-amber-100 text-amber-700',
  'Tam tahsilat': 'bg-green-100 text-green-700'
};
const getStatusColor = (s) => STATUS_COLORS[s] || 'bg-gray-100 text-gray-600';

const TABS = [
  { key: 'calls', label: 'Görüşmeler', icon: Phone },
  { key: 'quotes', label: 'Teklif', icon: FileText },
  { key: 'renewals', label: 'Yenileme', icon: RefreshCw },
  { key: 'payments', label: 'Tahsilat', icon: CreditCard },
  { key: 'history', label: 'İşlem Geçmişi', icon: History }
];

const ACTIVITY_STYLE = {
  renewal: ['bg-green-100', CheckCircle, 'text-green-600'],
  renewal_edit: ['bg-amber-100', Pencil, 'text-amber-600'],
  payment: ['bg-emerald-100', CreditCard, 'text-emerald-600'],
  payment_edit: ['bg-amber-100', Pencil, 'text-amber-600'],
  call: ['bg-blue-100', Phone, 'text-blue-600'],
  quote: ['bg-purple-100', FileText, 'text-purple-600'],
  status_change: ['bg-gray-100', History, 'text-gray-600'],
  case_created: ['bg-gray-100', Plus, 'text-gray-600']
};
const activityStyle = (t) => ACTIVITY_STYLE[t] || (t.includes('cancel') || t.includes('delete') ? ['bg-red-100', X, 'text-red-600'] : ['bg-gray-100', History, 'text-gray-600']);

const SummaryCard = ({ label, value, sub, color = 'text-gray-800', testId }) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-lg md:text-xl font-bold ${color}`} data-testid={testId}>{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </CardContent>
  </Card>
);

const RowActions = ({ canModify, onEdit, onCancel, onDelete, id }) => {
  if (!canModify) return null;
  return (
    <div className="flex gap-1">
      {onEdit && <Button data-testid={`edit-btn-${id}`} size="sm" variant="ghost" className="h-7 px-2" onClick={onEdit}><Pencil className="w-3.5 h-3.5" /></Button>}
      {onCancel && <Button data-testid={`cancel-btn-${id}`} size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={onCancel}><Ban className="w-3.5 h-3.5" /></Button>}
      {onDelete && <Button data-testid={`delete-btn-${id}`} size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={onDelete}><Trash2 className="w-3.5 h-3.5" /></Button>}
    </div>
  );
};

const CancelledNote = ({ r }) => r.is_cancelled ? (
  <p className="text-xs text-red-600 mt-1">İptal edildi ({r.cancelled_by}, {formatDateTime(r.cancelled_at)}): {r.cancelled_reason}</p>
) : null;

const EmptyText = ({ children }) => <p className="text-gray-500 text-center py-8">{children}</p>;

export default function SubscriptionManagement({ user }) {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calls');
  const [dialog, setDialog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reasonAction, setReasonAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const requestIdRef = useRef(null);
  const token = localStorage.getItem('token');

  const [callForm, setCallForm] = useState({ call_result: '', contact_person: '', notes: '', next_call_date: '' });
  const [quoteForm, setQuoteForm] = useState({ quote_amount: '', quote_date: todayStr(), validity_date: '', description: '' });
  const [statusForm, setStatusForm] = useState({ operation_status: '', next_followup_date: '', not_renewing_reason: '', not_renewing_note: '' });

  const loadCaseDetail = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}?token=${token}`);
      setCaseData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dosya yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [caseId, token]);

  useEffect(() => { loadCaseDetail(); }, [loadCaseDetail]);

  const openDialog = (name, record = null) => {
    requestIdRef.current = newRequestId();
    setEditing(record);
    setDialog(name);
  };
  const closeDialog = () => { setDialog(null); setEditing(null); };

  const run = async (fn, successMsg, after) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fn();
      toast.success(res?.data?.duplicate ? 'Bu kayıt zaten eklenmişti' : successMsg);
      after?.();
      await loadCaseDetail();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const canModify = (r) => user?.role === 'admin' || r.created_by === user?.username;

  const handleCallSubmit = () => {
    if (!callForm.call_result) return toast.error('Arama sonucu seçiniz');
    if (callForm.call_result === 'Görüşüldü' && !callForm.notes.trim()) return toast.error('Görüşüldü seçildiğinde görüşme notu zorunludur');
    if (['Ulaşılamadı', 'Meşgul'].includes(callForm.call_result) && !callForm.next_call_date) return toast.error('Ulaşılamadı veya Meşgul seçildiğinde tekrar arama tarihi zorunludur');
    if (callForm.next_call_date && new Date(callForm.next_call_date) < new Date()) return toast.error('Tekrar arama tarihi geçmişte olamaz');
    run(() => axios.post(`${API}/call-logs?token=${token}`, {
      case_id: caseId, ...callForm, request_id: requestIdRef.current,
      next_call_date: callForm.next_call_date ? new Date(callForm.next_call_date).toISOString() : null
    }), 'Arama kaydı eklendi', () => { closeDialog(); setCallForm({ call_result: '', contact_person: '', notes: '', next_call_date: '' }); });
  };

  const handleQuoteSubmit = () => {
    const amount = Math.round(parseFloat(String(quoteForm.quote_amount).replace(',', '.')) * 100);
    if (!quoteForm.quote_amount || !(amount > 0)) return toast.error('Teklif tutarı giriniz');
    if (!quoteForm.validity_date) return toast.error('Geçerlilik tarihi zorunludur');
    if (quoteForm.validity_date < quoteForm.quote_date) return toast.error('Geçerlilik tarihi teklif tarihinden önce olamaz');
    run(() => axios.post(`${API}/quotes?token=${token}`, {
      case_id: caseId, quote_amount: amount, quote_date: quoteForm.quote_date,
      validity_date: quoteForm.validity_date, description: quoteForm.description, request_id: requestIdRef.current
    }), 'Teklif eklendi', () => { closeDialog(); setQuoteForm({ quote_amount: '', quote_date: todayStr(), validity_date: '', description: '' }); });
  };

  const handleRenewalSubmit = (payload, error) => {
    if (error) return toast.error(error);
    const req = editing
      ? () => axios.put(`${API}/renewals/${editing.id}?token=${token}`, payload)
      : () => axios.post(`${API}/renewals?token=${token}`, { case_id: caseId, ...payload, request_id: requestIdRef.current });
    run(req, editing ? 'Yenileme düzeltildi' : 'Yenileme eklendi', closeDialog);
  };

  const handlePaymentSubmit = (payload, error) => {
    if (error) return toast.error(error);
    const req = editing
      ? () => axios.put(`${API}/payments/${editing.id}?token=${token}`, payload)
      : () => axios.post(`${API}/payments?token=${token}`, { case_id: caseId, ...payload, request_id: requestIdRef.current });
    run(req, editing ? 'Tahsilat düzeltildi' : 'Tahsilat eklendi', closeDialog);
  };

  const handleStatusSubmit = () => {
    const f = statusForm;
    if (!f.operation_status) return toast.error('Durum seçiniz');
    if (f.operation_status === 'Ertelendi' && !f.next_followup_date) return toast.error('Yeni takip tarihi zorunludur');
    if (f.operation_status === 'Yenilemeyecek' && (!f.not_renewing_reason || !f.not_renewing_note.trim())) return toast.error('Neden ve açıklama zorunludur');
    const params = new URLSearchParams({ token, operation_status: f.operation_status });
    if (f.next_followup_date) params.append('next_followup_date', new Date(`${f.next_followup_date}T09:00:00+03:00`).toISOString());
    if (f.not_renewing_reason) params.append('not_renewing_reason', f.not_renewing_reason);
    if (f.not_renewing_note) params.append('not_renewing_note', f.not_renewing_note);
    run(() => axios.put(`${API}/cases/${caseId}/status?${params.toString()}`), 'Durum güncellendi',
      () => { closeDialog(); setStatusForm({ operation_status: '', next_followup_date: '', not_renewing_reason: '', not_renewing_note: '' }); });
  };

  const REASON_ACTIONS = {
    cancelRenewal: { title: 'Yenilemeyi İptal Et', label: 'İptal Et', req: (id, reason) => axios.put(`${API}/renewals/${id}/cancel?token=${token}&reason=${encodeURIComponent(reason)}`), msg: 'Yenileme iptal edildi' },
    cancelPayment: { title: 'Tahsilatı İptal Et', label: 'İptal Et', req: (id, reason) => axios.put(`${API}/payments/${id}/cancel?token=${token}&reason=${encodeURIComponent(reason)}`), msg: 'Tahsilat iptal edildi' },
    deleteCall: { title: 'Arama Kaydını Sil', label: 'Sil', req: (id, reason) => axios.delete(`${API}/call-logs/${id}?token=${token}&reason=${encodeURIComponent(reason)}`), msg: 'Arama kaydı silindi' },
    deleteQuote: { title: 'Teklifi Sil', label: 'Sil', req: (id, reason) => axios.delete(`${API}/quotes/${id}?token=${token}&reason=${encodeURIComponent(reason)}`), msg: 'Teklif silindi' }
  };
  const currentReason = reasonAction && REASON_ACTIONS[reasonAction.type];
  const confirmReason = (reason) => run(() => currentReason.req(reasonAction.id, reason), currentReason.msg, () => setReasonAction(null));

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  if (!caseData) {
    return (
      <div className="p-6 text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700">Dosya bulunamadı</h2>
        <Button onClick={() => navigate('/subscription-management')} className="mt-4">Geri Dön</Button>
      </div>
    );
  }

  const remainingDevices = caseData.source_device_count - caseData.total_renewed_devices;
  const isClosed = caseData.operation_status === 'Yenilemeyecek' || caseData.renewal_status === 'Tam yenilendi';
  const isOverdue = !isClosed && caseData.next_followup_date && new Date(caseData.next_followup_date) < new Date();
  const counts = {
    calls: caseData.call_logs?.length, quotes: caseData.quotes?.length,
    renewals: caseData.renewals?.filter(r => !r.is_cancelled).length,
    payments: caseData.payments?.filter(p => !p.is_cancelled).length, history: caseData.activities?.length
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen" data-testid="subscription-case-detail">
      <div className="flex items-start md:items-center gap-4 mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl md:text-2xl font-bold text-gray-800" data-testid="case-customer-name">{caseData.customer_name}</h1>
            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{caseData.period}</span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 flex-wrap">
            <span className="flex items-center gap-1"><User className="w-4 h-4" />{caseData.representative}</span>
            <span data-testid="case-operation-status" className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(caseData.operation_status)}`}>{caseData.operation_status}</span>
            <span data-testid="case-renewal-status" className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(caseData.renewal_status)}`}>{caseData.renewal_status}</span>
            <span data-testid="case-collection-status" className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(caseData.collection_status)}`}>{caseData.collection_status}</span>
          </div>
        </div>
        <Button data-testid="change-status-btn" onClick={() => openDialog('status')} variant="outline" size="sm">Durum Değiştir</Button>
      </div>

      {isOverdue && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="overdue-warning">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-700">Gecikmiş Takip!</p>
            <p className="text-sm text-red-600">Planlanan takip: {formatDateTime(caseData.next_followup_date)}</p>
          </div>
        </div>
      )}

      {caseData.operation_status === 'Yenilemeyecek' && caseData.not_renewing_reason && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 text-sm text-red-700" data-testid="not-renewing-info">
          <b>Yenilemeyecek:</b> {caseData.not_renewing_reason} — {caseData.not_renewing_note}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <SummaryCard label="Aboneliği Dolan" value={caseData.source_device_count} sub="cihaz" testId="summary-source-devices" />
        <SummaryCard label="Yenilenen" value={`${caseData.total_renewed_devices}/${caseData.source_device_count}`} sub={`%${String(caseData.renewal_percentage).replace('.', ',')}`} color="text-blue-600" testId="summary-renewed-devices" />
        <SummaryCard label="Beklenen Gelir" value={formatCurrency(caseData.expected_revenue)} testId="summary-expected-revenue" />
        <SummaryCard label="Gerçekleşen" value={formatCurrency(caseData.total_renewed_amount)} color="text-green-600" testId="summary-renewed-amount" />
        <SummaryCard label="Tahsil Edilen" value={formatCurrency(caseData.total_collected)} color="text-emerald-600" testId="summary-collected" />
        <SummaryCard label="Kalan Bakiye" value={formatCurrency(caseData.remaining_balance)} color={caseData.remaining_balance > 0 ? 'text-amber-600' : 'text-green-600'} testId="summary-remaining-balance" />
      </div>

      {TERMS.some(t => termTotal(caseData, t) > 0) && (
        <div className="flex flex-wrap gap-2 mb-6 text-sm" data-testid="case-term-prices">
          <span className="text-gray-500">Liste fiyatları (tüm cihazlar):</span>
          {TERMS.map(t => (
            <span key={t} className="bg-white border rounded-full px-3 py-0.5 text-gray-700" data-testid={`case-term-price-${t}`}>
              {t} yıl: <b>{formatCurrency(termTotal(caseData, t))}</b>
            </span>
          ))}
        </div>
      )}

      <Card className="mb-6">
        <CardContent className="p-4 flex flex-wrap gap-6 text-sm">
          <span className="flex items-center gap-2 text-gray-600"><User className="w-4 h-4 text-gray-400" />{caseData.contact_person || '-'}</span>
          <a href={`tel:${caseData.contact_phone}`} className="flex items-center gap-2 text-blue-600 hover:underline"><Phone className="w-4 h-4 text-gray-400" />{caseData.contact_phone || '-'}</a>
          <span className="flex items-center gap-2 text-gray-600"><Calendar className="w-4 h-4 text-gray-400" />Bitiş: {formatDate(caseData.subscription_end_date)}</span>
          {caseData.next_followup_date && (
            <span className={`flex items-center gap-2 ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}`} data-testid="case-next-followup">
              <Clock className="w-4 h-4 text-gray-400" />Sonraki Takip: {formatDateTime(caseData.next_followup_date)}
            </span>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {TABS.map(({ key, label, icon: Icon }) => (
          <Button key={key} data-testid={`tab-${key}`} variant={activeTab === key ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab(key)} className="flex items-center gap-2 whitespace-nowrap">
            <Icon className="w-4 h-4" />{label}{counts[key] ? <span className="text-xs opacity-70">({counts[key]})</span> : null}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-4">
          {activeTab === 'calls' && (
            <div data-testid="calls-tab">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Arama Kayıtları</h3>
                <Button data-testid="add-call-btn" onClick={() => openDialog('call')} size="sm" className="flex items-center gap-2"><Plus className="w-4 h-4" />Arama Kaydı Ekle</Button>
              </div>
              {!caseData.call_logs?.length ? <EmptyText>Henüz arama kaydı yok</EmptyText> : (
                <div className="space-y-3">
                  {caseData.call_logs.map(call => (
                    <div key={call.id} className="border rounded-lg p-3 hover:bg-gray-50" data-testid={`call-row-${call.id}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${call.call_result === 'Görüşüldü' ? 'bg-green-100 text-green-700' : call.call_result === 'Ulaşılamadı' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>{call.call_result}</span>
                          {call.contact_person && <span className="ml-2 text-sm text-gray-600">{call.contact_person}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{formatDateTime(call.call_time)} • {call.created_by}</span>
                          <RowActions id={call.id} canModify={canModify(call)} onDelete={() => setReasonAction({ type: 'deleteCall', id: call.id })} />
                        </div>
                      </div>
                      {call.notes && <p className="mt-2 text-sm text-gray-600">{call.notes}</p>}
                      {call.next_call_date && <p className="mt-1 text-xs text-blue-600">Tekrar arama: {formatDateTime(call.next_call_date)}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'quotes' && (
            <div data-testid="quotes-tab">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Teklifler</h3>
                <Button data-testid="add-quote-btn" onClick={() => openDialog('quote')} size="sm" className="flex items-center gap-2"><Plus className="w-4 h-4" />Teklif Ekle</Button>
              </div>
              {!caseData.quotes?.length ? <EmptyText>Henüz teklif yok</EmptyText> : (
                <div className="space-y-2">
                  {caseData.quotes.map(quote => {
                    const expired = quote.validity_date < todayStr();
                    return (
                      <div key={quote.id} className="border rounded-lg p-3 flex justify-between items-center gap-2" data-testid={`quote-row-${quote.id}`}>
                        <div>
                          <span className="font-semibold text-gray-800">{formatCurrency(quote.quote_amount)}</span>
                          {expired && <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Süresi doldu</span>}
                          {quote.description && <p className="text-sm text-gray-500">{quote.description}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right text-xs text-gray-400">
                            <p>Tarih: {formatDate(quote.quote_date)}</p>
                            <p>Geçerlilik: {formatDate(quote.validity_date)}</p>
                            <p>{quote.created_by}</p>
                          </div>
                          <RowActions id={quote.id} canModify={canModify(quote)} onDelete={() => setReasonAction({ type: 'deleteQuote', id: quote.id })} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'renewals' && (
            <div data-testid="renewals-tab">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Yenilemeler</h3>
                <Button data-testid="add-renewal-btn" onClick={() => openDialog('renewal')} size="sm" className="flex items-center gap-2" disabled={remainingDevices <= 0}><Plus className="w-4 h-4" />Yenileme Ekle</Button>
              </div>
              <div className={`rounded-lg p-3 mb-4 text-sm border ${remainingDevices > 0 ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-green-50 border-green-200 text-green-700'}`} data-testid="renewal-remaining-banner">
                {remainingDevices > 0 ? <><b>{remainingDevices}</b> cihaz henüz yenilenmedi</> : 'Tüm cihazlar yenilendi'}
              </div>
              {caseData.devices?.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-1" data-testid="case-device-chips">
                  {(() => {
                    const renewed = new Set(caseData.renewals.filter(r => !r.is_cancelled).flatMap(r => r.device_ids || []));
                    return caseData.devices.map(d => (
                      <span key={d} className={`text-xs px-2 py-0.5 rounded border ${renewed.has(d) ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-200 text-gray-500'}`}>{d}</span>
                    ));
                  })()}
                </div>
              )}
              {!caseData.renewals?.length ? <EmptyText>Henüz yenileme yok</EmptyText> : (
                <div className="space-y-2">
                  {caseData.renewals.map(r => (
                    <div key={r.id} className={`border rounded-lg p-3 ${r.is_cancelled ? 'opacity-60 bg-gray-50' : ''}`} data-testid={`renewal-row-${r.id}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div className={r.is_cancelled ? 'line-through' : ''}>
                          <span className="font-semibold text-green-600">{r.device_count} cihaz</span>
                          {r.term_years && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded no-underline" data-testid={`renewal-term-badge-${r.id}`}>{r.term_years} yıl</span>}
                          <span className="mx-2">•</span>
                          <span className="font-semibold text-gray-800">{formatCurrency(r.renewal_amount)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{formatDate(r.renewal_date)} • {r.created_by}</span>
                          {!r.is_cancelled && <RowActions id={r.id} canModify={canModify(r)} onEdit={() => openDialog('renewal', r)} onCancel={() => setReasonAction({ type: 'cancelRenewal', id: r.id })} />}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Yeni dönem: {formatDate(r.new_subscription_start)} - {formatDate(r.new_subscription_end)}</p>
                      {r.device_ids?.length > 0 && <p className="text-xs text-gray-500 mt-1">Cihazlar: {r.device_ids.join(', ')}</p>}
                      {r.description && <p className="text-sm text-gray-600 mt-1">{r.description}</p>}
                      {r.updated_by && <p className="text-xs text-amber-600 mt-1">Düzeltildi: {r.updated_by}, {formatDateTime(r.updated_at)}</p>}
                      <CancelledNote r={r} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <div data-testid="payments-tab">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Tahsilatlar</h3>
                <Button data-testid="add-payment-btn" onClick={() => openDialog('payment')} size="sm" className="flex items-center gap-2" disabled={caseData.total_renewed_amount === 0 || caseData.remaining_balance <= 0}><Plus className="w-4 h-4" />Tahsilat Ekle</Button>
              </div>
              {caseData.total_renewed_amount === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-700" data-testid="payment-no-renewal-warning">Yenileme olmadan tahsilat eklenemez</div>
              )}
              <div className="bg-gray-50 rounded-lg p-3 mb-4 flex justify-between items-center text-sm">
                <span className="text-gray-600">Yenileme: <b>{formatCurrency(caseData.total_renewed_amount)}</b> • Tahsil: <b>{formatCurrency(caseData.total_collected)}</b> • Kalan: <b>{formatCurrency(caseData.remaining_balance)}</b></span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(caseData.collection_status)}`}>{caseData.collection_status}</span>
              </div>
              {!caseData.payments?.length ? <EmptyText>Henüz tahsilat yok</EmptyText> : (
                <div className="space-y-2">
                  {caseData.payments.map(p => (
                    <div key={p.id} className={`border rounded-lg p-3 ${p.is_cancelled ? 'opacity-60 bg-gray-50' : ''}`} data-testid={`payment-row-${p.id}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <span className={`font-semibold text-emerald-600 ${p.is_cancelled ? 'line-through' : ''}`}>{formatCurrency(p.payment_amount)}</span>
                          <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{p.payment_method}</span>
                          {p.reference && <p className="text-xs text-gray-500 mt-1">Ref: {p.reference}</p>}
                          {p.description && <p className="text-sm text-gray-600 mt-1">{p.description}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{formatDate(p.payment_date)} • {p.created_by}</span>
                          {!p.is_cancelled && <RowActions id={p.id} canModify={canModify(p)} onEdit={() => openDialog('payment', p)} onCancel={() => setReasonAction({ type: 'cancelPayment', id: p.id })} />}
                        </div>
                      </div>
                      {p.updated_by && <p className="text-xs text-amber-600 mt-1">Düzeltildi: {p.updated_by}, {formatDateTime(p.updated_at)}</p>}
                      <CancelledNote r={p} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div data-testid="history-tab">
              <h3 className="font-semibold text-gray-700 mb-4">İşlem Geçmişi</h3>
              {!caseData.activities?.length ? <EmptyText>Henüz işlem yok</EmptyText> : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  <div className="space-y-4">
                    {caseData.activities.map(a => {
                      const [bg, Icon, color] = activityStyle(a.activity_type);
                      return (
                        <div key={a.id} className="relative pl-10" data-testid={`activity-row-${a.id}`}>
                          <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${bg}`}><Icon className={`w-3 h-3 ${color}`} /></div>
                          <div className="bg-white border rounded-lg p-3">
                            <div className="flex justify-between items-start gap-2">
                              <p className="text-sm text-gray-700">{a.description}</p>
                              <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(a.created_at)}</span>
                            </div>
                            {(a.related_amount != null || a.related_count != null) && (
                              <p className="text-sm font-medium text-gray-600 mt-1">
                                {a.related_amount != null && `Tutar: ${formatCurrency(a.related_amount)}`}
                                {a.related_count != null && ` • ${a.related_count} adet`}
                              </p>
                            )}
                            {a.old_value && a.new_value && <p className="text-xs text-gray-500 mt-1">{a.old_value} → {a.new_value}</p>}
                            {a.reason && <p className="text-xs text-red-600 mt-1">Gerekçe: {a.reason}</p>}
                            <p className="text-xs text-gray-400 mt-1">İşlemi yapan: {a.created_by}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400 text-center mt-4">* Tutarlar KDV dahildir • Saatler Türkiye saatine göredir</p>

      <Dialog open={dialog === 'call'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-md" data-testid="call-dialog">
          <DialogHeader><DialogTitle>Arama Kaydı Ekle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Arama Sonucu *</Label>
              <Select value={callForm.call_result} onValueChange={(v) => setCallForm({ ...callForm, call_result: v })}>
                <SelectTrigger data-testid="call-result-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                <SelectContent>
                  {['Görüşüldü', 'Ulaşılamadı', 'Meşgul', 'Yanlış numara'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Görüşülen Kişi</Label>
              <Input data-testid="call-contact-input" value={callForm.contact_person} onChange={(e) => setCallForm({ ...callForm, contact_person: e.target.value })} placeholder="Kişi adı" />
            </div>
            <div>
              <Label>Görüşme Notu {callForm.call_result === 'Görüşüldü' && '*'}</Label>
              <Textarea data-testid="call-notes-input" value={callForm.notes} onChange={(e) => setCallForm({ ...callForm, notes: e.target.value })} rows={3} />
            </div>
            <div>
              <Label>Tekrar Arama Tarihi {['Ulaşılamadı', 'Meşgul'].includes(callForm.call_result) && '*'}</Label>
              <Input data-testid="call-next-date-input" type="datetime-local" value={callForm.next_call_date} onChange={(e) => setCallForm({ ...callForm, next_call_date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>İptal</Button>
            <Button data-testid="call-submit-btn" onClick={handleCallSubmit} disabled={submitting}>{submitting ? 'Kaydediliyor...' : 'Kaydet'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'quote'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-md" data-testid="quote-dialog">
          <DialogHeader><DialogTitle>Teklif Ekle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Teklif Tutarı (TL, KDV dahil) *</Label>
              <Input data-testid="quote-amount-input" type="number" min="0" step="0.01" value={quoteForm.quote_amount} onChange={(e) => setQuoteForm({ ...quoteForm, quote_amount: e.target.value })} placeholder="0" />
            </div>
            <div>
              <Label>Teklif Tarihi</Label>
              <Input data-testid="quote-date-input" type="date" value={quoteForm.quote_date} onChange={(e) => setQuoteForm({ ...quoteForm, quote_date: e.target.value })} />
            </div>
            <div>
              <Label>Geçerlilik Tarihi *</Label>
              <Input data-testid="quote-validity-input" type="date" min={quoteForm.quote_date} value={quoteForm.validity_date} onChange={(e) => setQuoteForm({ ...quoteForm, validity_date: e.target.value })} />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea data-testid="quote-description-input" value={quoteForm.description} onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>İptal</Button>
            <Button data-testid="quote-submit-btn" onClick={handleQuoteSubmit} disabled={submitting}>{submitting ? 'Kaydediliyor...' : 'Kaydet'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RenewalDialog open={dialog === 'renewal'} onClose={closeDialog} onSubmit={handleRenewalSubmit} submitting={submitting} caseData={caseData} editing={editing} />
      <PaymentDialog open={dialog === 'payment'} onClose={closeDialog} onSubmit={handlePaymentSubmit} submitting={submitting} caseData={caseData} editing={editing} />

      <Dialog open={dialog === 'status'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-md" data-testid="status-dialog">
          <DialogHeader><DialogTitle>Durum Değiştir</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Yeni Durum *</Label>
              <Select value={statusForm.operation_status} onValueChange={(v) => setStatusForm({ ...statusForm, operation_status: v })}>
                <SelectTrigger data-testid="status-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                <SelectContent>
                  {['Aranacak', 'Takipte', 'Teklif verildi', 'Ertelendi', 'Yenilemeyecek'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {statusForm.operation_status === 'Ertelendi' && (
              <div>
                <Label>Yeni Takip Tarihi *</Label>
                <Input data-testid="status-followup-input" type="date" min={todayStr()} value={statusForm.next_followup_date} onChange={(e) => setStatusForm({ ...statusForm, next_followup_date: e.target.value })} />
              </div>
            )}
            {statusForm.operation_status === 'Yenilemeyecek' && (
              <>
                <div>
                  <Label>Neden *</Label>
                  <Select value={statusForm.not_renewing_reason} onValueChange={(v) => setStatusForm({ ...statusForm, not_renewing_reason: v })}>
                    <SelectTrigger data-testid="status-reason-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                    <SelectContent>
                      {['Fiyat', 'Hizmet memnuniyetsizliği', 'Rakip firma', 'İşi bıraktı', 'İletişim kurulamıyor', 'Diğer'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Açıklama *</Label>
                  <Textarea data-testid="status-note-input" value={statusForm.not_renewing_note} onChange={(e) => setStatusForm({ ...statusForm, not_renewing_note: e.target.value })} rows={3} />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>İptal</Button>
            <Button data-testid="status-submit-btn" onClick={handleStatusSubmit} disabled={submitting}>{submitting ? 'Kaydediliyor...' : 'Kaydet'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReasonDialog
        open={!!reasonAction}
        title={currentReason?.title}
        actionLabel={currentReason?.label}
        onClose={() => setReasonAction(null)}
        onConfirm={confirmReason}
        submitting={submitting}
      />
    </div>
  );
}
